/**
 * Вкладка «Без товара» каталога пряников: пряники с макетом и файлами печати, у которых нет товара PP0
 * (режим «Создать только пряники» мастера «Создать пряники»). Выборка идёт из таблицы пряников,
 * а не из каталога товаров, поэтому у вкладки свой поиск, своя пагинация и свой выбор строк.
 * Массовое «Создать товары» (право Gingerbread:APPROVE) ставит фоновую задачу и после её конца
 * обновляет вкладку и счётчики каталога.
 * ТЗ: docs/SPEC_2026-10-04_GINGERBREAD_CREATE_WIZARD.md §5.5.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, PackagePlus, RefreshCw } from 'lucide-react'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { EmptyState } from '../../../components/ui/EmptyState'
import { FloatingBulkBar } from '../../../components/ui/FloatingBulkBar'
import { SearchInput } from '../../../components/ui/SearchInput'
import { TableSkeleton } from '../../../components/ui/Skeleton'
import { Pagination, Table, type Column } from '../../../components/ui/Table'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'
import { useSearchQuery } from '../../../shared/hooks/useSearchQuery'
import { useToastStore } from '../../../stores/toastStore'
import { useEntityNavigator } from '../../../components/entity-view/navigatorContext'
import { useGingerbreadJobs } from '../../gingerbread-generator/hooks/useGingerbreadJobs'
import { formatSizeMm, formatWeight } from '../../gingerbread-studio/format'
import { formatDate, formatNumber } from '../../products-v2/lib'
import { gingerbreadCatalogAPI } from '../api'
import { jobHref } from '../lib'
import type { NoProductRow } from '../types'
import { GingerbreadBlankWindow } from './GingerbreadBlankWindow'

const PAGE_SIZE_OPTIONS = [25, 50, 100]
/** Предел одного запуска: идентификаторы уходят в тело POST. */
const SELECTION_LIMIT = 500
/** Сколько ждём появления задачи в списке фоновых задач, прежде чем перестать показывать полосу. */
const UNSEEN_JOB_TIMEOUT_MS = 30_000

const muted = 'text-gray-400 dark:text-zinc-500'
const barButton =
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-gray-200 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40'

/** Адрес превью пряника: готовый файл `preview_png` (гейтвей отдаёт его по токену, поэтому через AuthorizedMediaImage). */
export const noProductPreviewUrl = (gingerbreadId: string): string =>
  `/api/v1/gingerbread/items/${encodeURIComponent(gingerbreadId)}/files/preview_png`

interface Props {
  canApprove: boolean
  /** Меняется, когда страница просит перечитать данные (кнопка «Обновить», конец других задач). */
  reloadTick: number
  /** Итог выборки — для счётчика вкладки. */
  onTotal: (total: number) => void
  /** Сколько строк показано из скольких в выборке (с учётом поиска): счётчик в заголовке страницы. */
  onShown?: (shown: number, total: number) => void
  /** Товары созданы: каталог и счётчики вкладок нужно перечитать. */
  onChanged: () => void
}

export function NoProductTab({ canApprove, reloadTick, onTotal, onShown, onChanged }: Props) {
  const showToast = useToastStore((state) => state.showToast)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [rows, setRows] = useState<NoProductRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [localTick, setLocalTick] = useState(0)
  const [selected, setSelected] = useState<Map<string, NoProductRow>>(new Map())
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [starting, setStarting] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)
  // Окно заготовки пряника (клик по строке) и пряник, для которого запущено создание из него.
  const [blank, setBlank] = useState<NoProductRow | null>(null)
  const createdFromWindow = useRef<NoProductRow | null>(null)
  const entityNav = useEntityNavigator()

  const { input, setInput, inputRef, clear } = useSearchQuery({
    value: search,
    onChange: (next) => {
      setSearch(next)
      setPage(1)
    },
  })

  const onTotalRef = useRef(onTotal)
  onTotalRef.current = onTotal
  const onShownRef = useRef(onShown)
  onShownRef.current = onShown

  // Выборка вкладки (а не каталога товаров) — в счётчик заголовка страницы; пока идёт загрузка, прежний счёт не врёт
  useEffect(() => {
    if (!loading) onShownRef.current?.(rows.length, total)
  }, [loading, rows.length, total])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setLoadError(null)
    gingerbreadCatalogAPI
      .getNoProduct({ page, limit: pageSize, search: search || undefined }, controller.signal)
      .then((result) => {
        setRows(result.items)
        setTotal(result.total)
        setLoading(false)
        // Счётчик вкладки — всей выборки без поиска; при поиске он показывал бы «найдено», а не «всего»
        if (!search) onTotalRef.current(result.total)
      })
      .catch((error: any) => {
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return
        setRows([])
        setTotal(0)
        setLoadError(error?.response?.data?.error?.message || error?.message || 'Не удалось загрузить пряники без товара')
        setLoading(false)
      })
    return () => controller.abort()
  }, [page, pageSize, search, reloadTick, localTick])

  const reload = useCallback(() => setLocalTick((tick) => tick + 1), [])

  // ── Выбор строк ──
  const allChecked = rows.length > 0 && rows.every((row) => selected.has(row.gingerbreadId))
  const someChecked = !allChecked && rows.some((row) => selected.has(row.gingerbreadId))
  const toggleRow = useCallback((row: NoProductRow, checked: boolean) => {
    setSelected((previous) => {
      const next = new Map(previous)
      if (checked) next.set(row.gingerbreadId, row)
      else next.delete(row.gingerbreadId)
      return next
    })
  }, [])
  const toggleAll = useCallback(
    (checked: boolean) => {
      setSelected((previous) => {
        const next = new Map(previous)
        for (const row of rows) {
          if (checked) next.set(row.gingerbreadId, row)
          else next.delete(row.gingerbreadId)
        }
        return next
      })
    },
    [rows],
  )

  // ── Создание товаров ──
  const requestCreate = () => {
    if (selected.size > SELECTION_LIMIT) {
      showToast(`За раз создаётся не больше ${SELECTION_LIMIT} товаров`, 'warning')
      return
    }
    setConfirmOpen(true)
  }
  const createProducts = async () => {
    setStarting(true)
    try {
      const result = await gingerbreadCatalogAPI.createProductsForNoProduct([...selected.keys()])
      setConfirmOpen(false)
      setSelected(new Map())
      showToast('Задача запущена: товары создаются в фоне. На маркетплейсы ничего не выгружается.', 'info')
      setJobId(result.jobId)
      if (!result.jobId) {
        reload()
        onChanged()
      }
    } catch (error: any) {
      showToast(
        `Не удалось создать товары: ${error?.response?.data?.error?.message || error?.message || 'ошибка'}`,
        'error',
      )
    } finally {
      setStarting(false)
    }
  }
  /** «Создать товар» из окна заготовки: то же действие, что массовое, для одного пряника. */
  const createOne = useCallback(
    async (row: NoProductRow) => {
      try {
        const result = await gingerbreadCatalogAPI.createProductsForNoProduct([row.gingerbreadId])
        showToast('Задача запущена: товар создаётся в фоне. На маркетплейсы ничего не выгружается.', 'info')
        createdFromWindow.current = row
        setJobId(result.jobId)
        if (!result.jobId) {
          createdFromWindow.current = null
          setBlank(null)
          reload()
          onChanged()
        }
      } catch (error: any) {
        showToast(
          `Не удалось создать товар: ${error?.response?.data?.error?.message || error?.message || 'ошибка'}`,
          'error',
        )
        throw error
      }
    },
    [onChanged, reload, showToast],
  )
  const onJobFinished = useCallback(
    (failed: boolean, unknown = false) => {
      setJobId(null)
      // Заготовка стала товаром: окно заготовки заменяется окном товара.
      const fromWindow = createdFromWindow.current
      createdFromWindow.current = null
      if (fromWindow && !failed && !unknown) {
        setBlank(null)
        if (fromWindow.articleReserved && entityNav.available) {
          entityNav.open({ kind: 'product', article: fromWindow.articleReserved })
        }
      }
      if (unknown) showToast('Ход задачи не получен: проверьте «Фоновые задачи». Список обновлён.', 'info')
      else {
        showToast(
          failed ? 'Создание товаров завершилось с ошибкой: проверьте вкладку и «Фоновые задачи»' : 'Товары созданы',
          failed ? 'error' : 'success',
        )
      }
      reload()
      onChanged()
    },
    [onChanged, reload, showToast, entityNav],
  )

  const columns = useMemo<Column<NoProductRow>[]>(
    () => [
      ...(canApprove
        ? [
            {
              key: 'select',
              width: '40px',
              header: (
                <input
                  type="checkbox"
                  aria-label="Выбрать все на странице"
                  checked={allChecked}
                  ref={(element) => {
                    if (element) element.indeterminate = someChecked
                  }}
                  onChange={(event) => toggleAll(event.target.checked)}
                  className="h-4 w-4 accent-indigo-600"
                />
              ),
              render: (_value: unknown, row: NoProductRow) => (
                <input
                  type="checkbox"
                  aria-label={`Выбрать ${row.designCode || row.name || row.gingerbreadId}`}
                  checked={selected.has(row.gingerbreadId)}
                  onChange={(event) => toggleRow(row, event.target.checked)}
                  className="h-4 w-4 accent-indigo-600"
                />
              ),
            } satisfies Column<NoProductRow>,
          ]
        : []),
      {
        key: 'preview',
        header: 'Превью',
        width: '72px',
        render: (_value, row) => (
          <span className="block h-11 w-11 overflow-hidden rounded border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
            <AuthorizedMediaImage
              src={noProductPreviewUrl(row.gingerbreadId)}
              alt={`Превью: ${row.name || row.designCode || row.gingerbreadId}`}
              className="h-full w-full object-contain"
            />
          </span>
        ),
      },
      {
        key: 'name',
        header: 'Название',
        width: '240px',
        render: (_value, row) => (
          <span className="block max-w-[240px] truncate text-[0.8125rem]" title={row.name ?? undefined}>
            {row.name || <span className={muted}>—</span>}
          </span>
        ),
      },
      {
        key: 'designCode',
        header: 'Код макета',
        width: '130px',
        render: (_value, row) =>
          row.designCode ? (
            <span className="font-mono text-[0.8125rem] font-semibold">{row.designCode}</span>
          ) : (
            <span className={muted}>—</span>
          ),
      },
      {
        key: 'article',
        header: 'Будущий артикул',
        width: '140px',
        render: (_value, row) =>
          row.articleReserved ? (
            <span className="font-mono text-[0.8125rem]">{row.articleReserved}</span>
          ) : (
            <span className={muted}>—</span>
          ),
      },
      {
        key: 'shape',
        header: 'Форма',
        width: '150px',
        render: (_value, row) => (
          <span className="block max-w-[150px] truncate text-[0.8125rem] text-gray-700 dark:text-zinc-300">
            {row.shapeMode === 'unified' ? row.shapeName || 'Справочник' : row.shapeMode === 'free' ? 'Свободная' : '—'}
          </span>
        ),
      },
      {
        key: 'size',
        header: 'Размер',
        width: '120px',
        render: (_value, row) => (
          <span className="whitespace-nowrap text-[0.8125rem] tabular-nums">{formatSizeMm(row.widthMm, row.heightMm)}</span>
        ),
      },
      {
        key: 'weight',
        header: 'Вес теста',
        width: '100px',
        render: (_value, row) => (
          <span className="whitespace-nowrap text-[0.8125rem] tabular-nums">{formatWeight(row.doughWeightG)}</span>
        ),
      },
      {
        key: 'job',
        header: 'Задание',
        width: '170px',
        render: (_value, row) =>
          row.jobId ? (
            <Link
              to={jobHref(row.jobId)}
              onClick={(event) => event.stopPropagation()}
              className="block max-w-[170px] truncate text-[0.8125rem] text-indigo-600 hover:underline dark:text-indigo-400"
              title={row.jobName ?? undefined}
            >
              {row.jobName || 'Открыть задание'}
            </Link>
          ) : (
            <span className={muted}>—</span>
          ),
      },
      {
        key: 'createdAt',
        header: 'Создан',
        width: '100px',
        render: (_value, row) => (
          <span className="whitespace-nowrap text-[0.8125rem] tabular-nums text-gray-600 dark:text-zinc-400">
            {formatDate(row.createdAt)}
          </span>
        ),
      },
    ],
    [canApprove, allChecked, someChecked, selected, toggleAll, toggleRow],
  )

  const lastPage = Math.max(1, Math.ceil(total / pageSize))

  return (
    <>
      <div className="flex shrink-0 flex-wrap items-center gap-2 px-5 pb-2.5 pt-3">
        <SearchInput
          ref={inputRef}
          value={input}
          onChange={setInput}
          onClear={clear}
          placeholder="Название, код макета, артикул"
          aria-label="Поиск пряников без товара"
          className="w-[19rem]"
        />
        <span className="text-xs text-gray-500 dark:text-zinc-400">
          Пряники с макетом и файлами печати, у которых ещё нет товара PP0
        </span>
        <button
          type="button"
          title="Обновить"
          aria-label="Обновить"
          onClick={reload}
          className="ml-auto inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-2.5 text-gray-500 hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {jobId && <NoProductJobBanner jobId={jobId} onFinished={onJobFinished} />}

      {selected.size > 0 && (
        <div className="shrink-0 border-y border-gray-200 bg-gray-50/50 px-5 py-1.5 text-2xs text-gray-500 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-400">
          Выбрано: <b className="tabular-nums">{formatNumber(selected.size)}</b>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        {loading ? (
          <TableSkeleton rows={8} columns={7} />
        ) : loadError ? (
          <EmptyState
            title="Не удалось загрузить пряники без товара"
            description={loadError}
            action={{ label: 'Повторить', onClick: reload, icon: <RefreshCw className="h-4 w-4" /> }}
          />
        ) : rows.length === 0 ? (
          search ? (
            <EmptyState title="Ничего не найдено" description="Измените запрос или очистите поиск" />
          ) : (
            <EmptyState
              title="Пряников без товара нет"
              description="Здесь появятся пряники, созданные мастером в режиме «Создать только пряники»"
            />
          )
        ) : (
          <div className="min-w-[1200px]">
            <Table
              columns={columns}
              data={rows}
              keyExtractor={(row) => row.gingerbreadId}
              onRowClick={(row: NoProductRow) => setBlank(row)}
              compact
              scrollX={false}
            />
          </div>
        )}
      </div>

      {!loading && total > 0 && (
        <Pagination
          currentPage={Math.min(page, lastPage)}
          totalPages={lastPage}
          totalItems={total}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      )}

      {canApprove && (
        <FloatingBulkBar count={selected.size} onClear={() => setSelected(new Map())}>
          <button type="button" onClick={requestCreate} disabled={starting} className={barButton}>
            <PackagePlus className="h-3.5 w-3.5" />
            Создать товары
          </button>
        </FloatingBulkBar>
      )}

      {blank && (
        <GingerbreadBlankWindow
          row={blank}
          previewUrl={noProductPreviewUrl(blank.gingerbreadId)}
          canApprove={canApprove}
          creating={jobId !== null}
          onCreate={createOne}
          onClose={() => setBlank(null)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Создать товары"
        message={`Для выбранных пряников (${formatNumber(selected.size)}) будут созданы товары PP0: с весом теста и файлами печати. На маркетплейсы ничего не выгружается.`}
        confirmLabel="Создать товары"
        loading={starting}
        onConfirm={() => void createProducts()}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  )
}

/** Ход фоновой задачи создания товаров; по окончании один раз сообщает наверх. */
function NoProductJobBanner({ jobId, onFinished }: { jobId: string; onFinished: (failed: boolean, unknown?: boolean) => void }) {
  const [job] = useGingerbreadJobs([jobId])
  const seen = useRef(false)
  const finished = useRef(false)
  const status = job?.status
  const active = status === 'pending' || status === 'processing'

  useEffect(() => {
    if (finished.current) return
    if (job) seen.current = true
    if (job && !active) {
      finished.current = true
      onFinished(status === 'failed' || status === 'cancelled' || status === 'stopped')
    } else if (!job && seen.current) {
      // Задача ушла из store после окончания: это тоже конец.
      finished.current = true
      onFinished(false)
    }
  }, [job, active, status, onFinished])

  // Задача создана бэкендом и приходит по WebSocket; если за полминуты её так и не видно — не висим вечно.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!seen.current && !finished.current) {
        finished.current = true
        onFinished(false, true)
      }
    }, UNSEEN_JOB_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [jobId, onFinished])

  const percentage = job?.progress?.percentage ?? 0
  return (
    <div
      role="status"
      data-testid="no-product-job-banner"
      className="mx-5 mb-2 space-y-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800 dark:border-indigo-900 dark:bg-indigo-950/30 dark:text-indigo-200"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {job?.currentStep || 'Создаём товары для пряников'}
        </span>
        <span className="font-mono text-xs">{percentage}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded bg-indigo-100 dark:bg-indigo-900/50">
        <div className="h-full bg-indigo-600 transition-all" style={{ width: `${Math.max(percentage, 3)}%` }} />
      </div>
    </div>
  )
}
